import React, { useEffect, useState } from 'react';
import { Provider, connect, useDispatch, useSelector } from "react-redux";
import { Box, CircularProgress } from '@mui/material';
import store, { history } from "./redux";
import { Route, Switch, Redirect } from "react-router-dom";
import { getUser, isAuthenticated, selectPermissions } from './redux/user/user.actions';
import Template from './template/Template';
import Login from './pages/Login';

import InProgressPage from './pages/InProgressPage';
import OperatorsPage from './pages/OperatorsPage';
import CreateOperatorPage from './pages/OperatorsPage/CreateOperatorPage';
import IndicatorPage from './pages/IndicatorPage';
import PendingDashboardPage from './pages/PendingDashboardPage';
import HistoryPage from './pages/HistoryPage';
import Profile from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import ProfilePermissionsEditPage from './pages/ProfilePermissionsEditPage';
import { ConnectedRouter } from 'connected-react-router';
import ToastContainer from './components/ToastContainer';
import ResetPassword from './pages/ResetPassword';
import TicketPage from './pages/TicketPage';
import TicketAttendanceReportPage from './pages/TicketAttendanceReportPage';
import AgendaPage from './pages/AgendaPage';
import NoticeBoardPage from './pages/NoticeBoardPage';
import TicketDetails from './components/TicketDetails';
import ClientTicketsPage from './pages/ClientTicketsPage';
import ClientSearchPage from './pages/ClientSearchPage';
import ClientGroupsPage from './pages/ClientGroupsPage';
import FiltersPage from './pages/FiltersPage';
import UserActivityLogsPage from './pages/UserActivityLogsPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import VideosDaBasePage from './pages/VideosDaBasePage';
import AttendancePanelPage from './pages/AttendancePanelPage';
import MovideskImportPage from './pages/MovideskImportPage';
import ClientImportPage from './pages/ClientImportPage';
import OrbyPage from './pages/OrbyPage';
import PlatformPage from './pages/PlatformPage';
import WhatsAppPage from './pages/WhatsAppPage';
import WhatsAppNumeroPage from './pages/WhatsAppNumeroPage';
import { NotificationProvider } from './contexts/NotificationContext';
import TestNotifications from './examples/TestNotifications';
import SessionExpiredModal from './components/SessionExpiredModal';
import { selectShowSessionExpiredModal } from './redux/user/user.actions';
import { hasAdminProfile } from './utils/profileRole';
import { PAGINA_DE_VIDEOS } from './utils/permissoesDaBase';

/**
 * Um componente de tela ESTAVEL por rota.
 *
 * Antes cada rota montava a tela com uma funcao de seta escrita ali mesmo, no
 * JSX. A funcao e criada de novo a cada render do `App`, e o React trata funcao
 * diferente como COMPONENTE diferente: desmonta a arvore inteira e monta
 * outra do zero, refazendo todos os efeitos de montagem.
 *
 * Na pratica, TODA tela do sistema carregava duas vezes. Medido em producao
 * na tela de chamados: `GET /ticket?scope=active` (3,9 s, 9,35 MB, 2.815
 * chamados) chegava DUAS vezes por abertura, junto com
 * `/notifications/ticket-panel` (3,4 s), `/users` e `/ticket-statuses`.
 *
 * Guardar o wrapper aqui fora do render resolve: mesmo `Template`, mesma
 * pagina, mesmas props -- o React so para de achar que virou outra coisa.
 *
 * Houve aqui um segundo parametro, `variante`, para dar wrappers DIFERENTES a
 * duas rotas que apontassem para a mesma pagina -- `/ticket/:id` e
 * `/ticket/new`. A justificativa era forcar o remonte ao ir de um chamado
 * para "novo". Nunca funcionou e nunca foi preciso: `/ticket/new` sempre foi
 * capturada pela rota com `:id` (e PRECISA ser, senao `isNewTicket` nao
 * existe), entao a segunda rota jamais casou e a variante jamais foi usada.
 * Conferido na tela em 29/10/2026: sem remonte nenhum, o formulario novo
 * nasce limpo. A rota morta saiu, e o parametro com ela.
 */
// A chave e a PROPRIA funcao da pagina, por identidade. Montar a chave a
// partir do nome quebraria no build de producao, onde o minificador renomeia
// os componentes e dois deles podem virar a mesma letra.
const telasPorRota = new Map();

export const tela = (Page) => {
  if (!telasPorRota.has(Page)) {
    const Tela = () => <Template Page={Page} />;
    telasPorRota.set(Page, Tela);
  }

  return telasPorRota.get(Page);
};

const PrivateRoute = ({ component: Component, ...params }) => {
  const dispatch = useDispatch();

  return (
    <Route
      {...params}
      render={props =>
        dispatch(isAuthenticated()) ? (
          <Component {...props} />
        ) : (
          <Redirect to={{ pathname: "/login", state: { from: props.location } }} />
        )
      }
    />
  );
};

const AdminRoute = ({ requiredPermission, component: Component, permissions, ...params }) => {
  const permissionsArray = Array.isArray(permissions) ? permissions : [];

  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    user = null;
  }
  const isAdmin = hasAdminProfile(user) || Boolean(user && user.is_super_admin);

  return (
    <Route
      {...params}
      render={props =>
        (isAdmin || permissionsArray.includes(requiredPermission)) ? (
          <Component {...props} />
        ) : (
          <Redirect to={{ pathname: "/" }} />
        )
      }
    />
  )
};

// Task 16 (Plataforma): gating de UI para a rota cross-tenant de
// super-admin. Diferente de AdminRoute (que também libera profile_id===1,
// ou seja, admin de UM tenant), esta rota exige estritamente
// `is_super_admin` -- só quem gerencia a plataforma inteira pode ver isso.
// É só conveniência: a barreira real é o middleware `super.admin` na API,
// que rejeita qualquer request sem essa flag independente do que a UI
// mostra ou esconde.
const SuperAdminRoute = ({ component: Component, ...params }) => {
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    user = null;
  }
  const isSuperAdmin = !!(user && user.is_super_admin);

  return (
    <Route
      {...params}
      render={props =>
        isSuperAdmin ? (
          <Component {...props} />
        ) : (
          <Redirect to={{ pathname: "/" }} />
        )
      }
    />
  );
};

const App = (props) => {
  const dispatch = useDispatch();
  const currentPermissions = useSelector(selectPermissions);
  const showSessionExpiredModal = useSelector(selectShowSessionExpiredModal);
  const [routeReady, setRouteReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      if (!cancelled) setRouteReady(true);
      return;
    }
    if (!token || !user?.id) {
      setRouteReady(true);
      return;
    }
    const perms = store.getState().user.permissions;
    if (Array.isArray(perms) && perms.length > 0) {
      setRouteReady(true);
      return;
    }
    dispatch(getUser(user.id))
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setRouteReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  let permissions = Array.isArray(currentPermissions) ? currentPermissions : [];

  if (!routeReady) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Usuário "somente Orby": NUNCA monta as rotas do AloDesk. Qualquer caminho cai no
  // /orby. Assim a plataforma AloDesk fica invisível para quem usa só o link do Orby.
  let orbyOnlyUser = null;
  try {
    orbyOnlyUser = JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    orbyOnlyUser = null;
  }
  if (orbyOnlyUser?.orby_only) {
    return (
      <Provider store={store}>
        <NotificationProvider>
          <ToastContainer />
          <SessionExpiredModal isOpen={showSessionExpiredModal} />
          <ConnectedRouter history={history} basename="/">
            <Switch>
              <Route exact path="/orby" component={OrbyPage} />
              <Route path="*" render={() => <Redirect to="/orby" />} />
            </Switch>
          </ConnectedRouter>
        </NotificationProvider>
      </Provider>
    );
  }

  return (
    <Provider store={store}>
      <NotificationProvider>
        <ToastContainer />
        <SessionExpiredModal isOpen={showSessionExpiredModal} />
        <ConnectedRouter history={history} basename="/">
        <Switch>
          <PrivateRoute
            exact
            path="/"
            permissions={permissions}
            component={tela(IndicatorPage)}
          />
          <PrivateRoute
            exact
            path="/dashboard-pendencias"
            permissions={permissions}
            component={tela(PendingDashboardPage)}
          />
          <PrivateRoute
            exact
            path="/chat"
            component={tela(InProgressPage)}
          />

          <PrivateRoute
            exact
            path="/perfil"
            component={tela(Profile)}
          />

          {/* Item 3: link standalone do Orby — rota PÚBLICA que cuida da própria
              autenticação (login do Orby se deslogado; Orby em tela cheia se logado). */}
          <Route
            exact
            path="/orby"
            component={OrbyPage}
          />

          <PrivateRoute
            exact
            path="/agenda"
            component={tela(AgendaPage)}
          />

          <PrivateRoute
            exact
            path="/mural-avisos"
            component={tela(NoticeBoardPage)}
          />

          {/* Os vídeos ANTES da base, e com permissão PRÓPRIA.
              Antes porque `/ia-base-conhecimento` é `exact` hoje, mas um
              `exact` removido numa refatoração futura faria ela engolir a
              rota filha -- e a tela de vídeos sumiria sem erro nenhum.

              A permissão era a mesma das duas telas, e isso tornava o pedido
              da Gisele impossível de configurar: "Operador: não apresentar a
              opção de IA/Base de Conhecimento" e "disponibilizar somente
              Vídeos e Treinamentos". Separar exigiu os três lugares de
              sempre -- `PermissionService`, a tela de permissões e um
              BACKFILL --, e é o backfill que impede a tela de sumir para todo
              mundo no deploy: ele copia a permissão para quem já tinha a
              base. */}
          <AdminRoute
            exact
            path="/ia-base-conhecimento/videos"
            requiredPermission={PAGINA_DE_VIDEOS}
            permissions={permissions}
            component={tela(VideosDaBasePage)}
          />

          <AdminRoute
            exact
            path="/ia-base-conhecimento"
            requiredPermission="/ia-base-conhecimento"
            permissions={permissions}
            component={tela(KnowledgeBasePage)}
          />

          <AdminRoute
            exact
            path="/whatsapp"
            requiredPermission="/whatsapp"
            permissions={permissions}
            component={tela(WhatsAppPage)}
          />

          {/* A tela de UM número. Mesma permissão da lista: é a mesma porta,
              e o `/whatsapp/:id` chega a token e segredo do cliente. */}
          <AdminRoute
            exact
            path="/whatsapp/:id"
            requiredPermission="/whatsapp"
            permissions={permissions}
            component={tela(WhatsAppNumeroPage)}
          />

          <PrivateRoute
            exact
            path="/ticket"
            component={tela(TicketPage)}
          />
          <AdminRoute
            exact
            path="/tempo-tickets"
            requiredPermission="/tempo-tickets"
            permissions={permissions}
            component={tela(TicketAttendanceReportPage)}
          />
          <PrivateRoute
            exact
            path="/filtros"
            component={tela(FiltersPage)}
          />

          {/*
            * `/ticket/new` ENTRA POR AQUI, e e assim que tem de ser.
            *
            * `TicketDetails` faz `const { id } = useParams()` e deriva
            * `isNewTicket = id === 'new'`. A tela de chamado novo so existe
            * porque esta rota captura "new" como id -- nao ha outra
            * verificacao.
            *
            * Existia abaixo uma rota literal `/ticket/new`, que nunca casava
            * por vir depois desta. Parecia o esquecimento classico (caminho
            * fixo escondido atras de um `:parametro`) e era o contrario: ao
            * "consertar" a ordem em 29/10/2026, o chamado novo abriu como
            * "#undefined - Sem assunto" -- casando com a rota sem parametro,
            * `id` vem `undefined` e `isNewTicket` vira false. A suite inteira
            * passou verde nesse estado; so a tela denunciou.
            *
            * A rota morta saiu para ninguem mais cair nela. O que guarda o
            * contrato agora e `App.rotas.test.js`, que cobra os PARAMETROS
            * que `/ticket/new` produz -- nao a ordem das linhas.
            */}
          <PrivateRoute
            exact
            path="/ticket/:id"
            component={tela(TicketDetails)}
          />

          <AdminRoute
            exact
            path="/cliente/:clientId/tickets"
            requiredPermission="/clientes/pesquisa"
            permissions={permissions}
            component={tela(ClientTicketsPage)}
          />

          <AdminRoute
            exact
            path="/clientes/pesquisa"
            requiredPermission="/clientes/pesquisa"
            permissions={permissions}
            component={tela(ClientSearchPage)}
          />

          <AdminRoute
            exact
            path="/clientes/grupos"
            requiredPermission="/clientes/pesquisa"
            permissions={permissions}
            component={tela(ClientGroupsPage)}
          />

          <AdminRoute
            exact
            path="/configuracoes/perfil/:profileId"
            requiredPermission="/configuracoes"
            permissions={permissions}
            component={tela(ProfilePermissionsEditPage)}
          />
          <AdminRoute
            exact
            path="/configuracoes"
            requiredPermission="/configuracoes"
            permissions={permissions}
            component={tela(SettingsPage)}
          />

          <AdminRoute
            exact
            path="/operadores"
            requiredPermission="/operadores"
            permissions={permissions}
            component={tela(OperatorsPage)}
          />

          <AdminRoute
            exact
            path="/novo-operador"
            requiredPermission="/novo-operador"
            permissions={permissions}
            component={tela(CreateOperatorPage)}
          />

          <AdminRoute
            exact
            path="/historico"
            requiredPermission="/historico"
            permissions={permissions}
            component={tela(HistoryPage)}
          />
          <AdminRoute
            exact
            path="/painel-atendimentos"
            requiredPermission="/painel-atendimentos"
            permissions={permissions}
            component={tela(AttendancePanelPage)}
          />
          <AdminRoute
            exact
            path="/importar-movidesk"
            requiredPermission="/importar-movidesk"
            permissions={permissions}
            component={tela(MovideskImportPage)}
          />
          <AdminRoute
            exact
            path="/importar-clientes"
            requiredPermission="/importar-clientes"
            permissions={permissions}
            component={tela(ClientImportPage)}
          />
          <AdminRoute
            exact
            path="/logs-atividades"
            requiredPermission="/logs-atividades"
            permissions={permissions}
            component={tela(UserActivityLogsPage)}
          />

          <SuperAdminRoute
            exact
            path="/plataforma"
            component={tela(PlatformPage)}
          />

          <PrivateRoute
            exact
            path="/teste-notificacoes"
            component={tela(TestNotifications)}
          />

          <Route path="/login" component={Login} />
          <Route path="/resetar-senha/:token" component={ResetPassword} />
        </Switch>
      </ConnectedRouter>
      </NotificationProvider>
    </Provider>
  )
};

const mapStateToProps = state => {
  const { user } = state;
  const { permissions } = user;

  return { permissions };
};

export default connect(mapStateToProps)(App);
